import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateUserPayload = {
  email: string;
  full_name: string;
};

export type UserSummary = {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  created_at: string;
};

/** Access-control payload returned for the logged-in user. */
export type UserAccess = {
  permissions: string[];
  /** Kebab-case tab names the user may view, in backend-defined order. */
  tabs: string[];
};

/** The logged-in user, including the access object the list/create endpoints do not return. */
export type LoggedInUser = UserSummary & {
  access: UserAccess;
};

/** An administrator-access assignment returned by the access-management endpoints. */
export type AccessEntry = {
  id: number;
  user_id: number;
  role_id: number;
  created_at: string;
};

export type UsersState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  users: UserSummary[];
  currentUser: LoggedInUser | null;
  currentUserStatus: "idle" | "loading" | "succeeded" | "failed";
  accesses: AccessEntry[];
};

export const usersInitialState: UsersState = {
  status: STATUS.IDLE,
  error: null,
  users: [],
  currentUser: null,
  currentUserStatus: STATUS.IDLE,
  accesses: [],
};

function isUserSummary(value: unknown): value is UserSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.email === "string" &&
    (typeof row.full_name === "string" || row.full_name === null) &&
    typeof row.is_active === "boolean" &&
    typeof row.created_at === "string"
  );
}

function isUserAccess(value: unknown): value is UserAccess {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const access = value as Record<string, unknown>;
  return (
    Array.isArray(access.permissions) &&
    access.permissions.every((permission) => typeof permission === "string") &&
    Array.isArray(access.tabs) &&
    access.tabs.every((tab) => typeof tab === "string")
  );
}

function isLoggedInUser(value: unknown): value is LoggedInUser {
  if (!isUserSummary(value)) {
    return false;
  }
  return isUserAccess((value as Record<string, unknown>).access);
}

function parseUsersResponse(data: unknown): UserSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isUserSummary)) {
    return null;
  }
  return data;
}

function isAccessEntry(value: unknown): value is AccessEntry {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const access = value as Record<string, unknown>;
  return (
    typeof access.id === "number" &&
    typeof access.user_id === "number" &&
    typeof access.role_id === "number" &&
    typeof access.created_at === "string"
  );
}

function parseAccessesResponse(data: unknown): AccessEntry[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isAccessEntry)) {
    return null;
  }
  return data;
}

export const createUser = createAsyncThunk<
  UserSummary,
  CreateUserPayload,
  { rejectValue: string }
>("users/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  LoggedInUser,
  void,
  { rejectValue: string }
>("users/fetchCurrentUser", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users/logged-in", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!isLoggedInUser(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type UpdateCurrentUserPayload = {
  full_name: string;
};

export const updateCurrentUser = createAsyncThunk<
  LoggedInUser,
  UpdateCurrentUserPayload,
  { rejectValue: string }
>("users/updateCurrentUser", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users/logged-in", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isLoggedInUser(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchUsers = createAsyncThunk<
  UserSummary[],
  void,
  { rejectValue: string }
>("users/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const users = parseUsersResponse(data);
    if (!users) {
      return rejectWithValue("Unexpected response shape");
    }
    return users;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteUser = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("users/delete", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return userId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchAccesses = createAsyncThunk<
  AccessEntry[],
  void,
  { rejectValue: string }
>("users/fetchAccesses", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/accesses", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const accesses = parseAccessesResponse(data);
    if (!accesses) {
      return rejectWithValue("Unexpected response shape");
    }
    return accesses;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const grantAccess = createAsyncThunk<
  AccessEntry,
  number,
  { rejectValue: string }
>("users/grantAccess", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/accesses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAccessEntry(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const revokeAccess = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("users/revokeAccess", async (accessId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/accesses/${accessId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return accessId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const usersSlice = createSlice({
  name: "users",
  initialState: usersInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.users = [];
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.currentUserStatus = STATUS.LOADING;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUserStatus = STATUS.SUCCEEDED;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.currentUserStatus = STATUS.FAILED;
      })
      .addCase(updateCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(fetchAccesses.fulfilled, (state, action) => {
        state.accesses = action.payload;
      })
      .addCase(fetchAccesses.rejected, (state) => {
        state.accesses = [];
      })
      .addCase(grantAccess.fulfilled, (state, action) => {
        state.accesses.push(action.payload);
      })
      .addCase(revokeAccess.fulfilled, (state, action) => {
        state.accesses = state.accesses.filter(
          (access) => access.id !== action.payload,
        );
      });
  },
});

export const usersReducer = usersSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.users.currentUser;
export const selectCurrentUserStatus = (state: RootState) => state.users.currentUserStatus;

/** Tabs the logged-in user may view, already filtered and ordered by the backend. */
export const selectAccessibleTabs = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.tabs ?? [],
);

/** API permissions for the logged-in user. Use to show/hide write actions on pages accessible to all users. */
export const selectAccessiblePermissions = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.permissions ?? [],
);

/** All administrator-access assignments for the tenant. */
export const selectAccesses = (state: RootState) => state.users.accesses;

/** Map of `user_id` → its administrator-access entry, for resolving a user's access state and its `id`. */
export const selectAccessByUserId = createSelector(selectAccesses, (accesses) => {
  const accessByUserId = new Map<number, AccessEntry>();
  for (const access of accesses) {
    accessByUserId.set(access.user_id, access);
  }
  return accessByUserId;
});
